import React from 'react';
import { PageHeader, Badge, Stat, type ToneType } from './shared';
import { ModalHeader, ModalShell } from './Modals';
import { Icon } from './ui/icons';

function cx(...classes: (string | undefined | false)[]): string {
  return classes.filter(Boolean).join(' ');
}

// Simple LangDot component
function LangDot({ lang }: { lang: string }) {
  const colors: Record<string, string> = {
    typescript: '#3178C6',
    javascript: '#F7DF1E',
    python: '#3776AB',
    go: '#00ADD8',
    rust: '#CE422B',
    java: '#ED8B00',
    kotlin: '#7F52FF',
    swift: '#FA7343',
    c: '#A8B9CC',
    cpp: '#00599C',
    csharp: '#239120'
  };
  
  return (
    <span 
      className="h-2 w-2 rounded-full" 
      style={{ background: colors[lang] || '#6B7280' }}
      title={lang}
    />
  );
}

// Simple ControlStatusBadge component
function ControlStatusBadge({ status }: { status: string }) {
  const tone: ToneType = status === 'published' ? 'ok' : 
                        status === 'draft' ? 'draft' :
                        status === 'changed' ? 'warn' : 'muted';
  return <Badge tone={tone}>{status}</Badge>;
}

// Tags — first-class typed taxonomy across the app.
//
// Every tag has a `kind` (compliance, environment, criticality, domain, behavior,
// custom). `behavior` tags in particular drive tag-based conformance rules — a
// control tagged `encryption-at-rest` automatically covers any requirement
// tagged `encryption`.

// ── Types and Interfaces ─────────────────────────────────────────────────────

interface TagKind {
  id: string;
  label: string;
  description: string;
  color: string;
}

interface Tag {
  id: string;
  kind: string;
  description?: string;
  appliesTo: string[];
}

interface Repository {
  id: string;
  name: string;
  lang: string;
  tags?: string[];
}

interface Control {
  id: string;
  name: string;
  status: string;
  tags?: string[];
}

interface Project {
  id: string;
  name: string;
  color: string;
  tags?: string[];
}

interface Framework {
  id: string;
  shortName: string;
  color: string;
  frameworks: any[];
}

interface ConformanceRule {
  id: string;
  name: string;
  kind: string;
  condition: {
    operands?: Array<{ tag?: string }>;
  };
}

interface Requirement {
  framework: string;
  id: string;
}

interface TagUsage {
  repos: Repository[];
  controls: Control[];
  projects: Project[];
  requirements: Requirement[];
  rules: ConformanceRule[];
}

interface TagData {
  tags?: {
    kinds: TagKind[];
    tags: Tag[];
  };
  repos?: {
    repos: Repository[];
  };
  controls?: {
    controls: Control[];
  };
  projects?: {
    projects: Project[];
  };
  frameworks: {
    frameworks: Framework[];
  };
  conformance?: {
    rules: ConformanceRule[];
  };
}

interface TagsProps {
  data: TagData;
  navigate: (route: string) => void;
  params?: string[];
}

interface TagPillProps {
  tagId: string;
  data: TagData;
  navigate?: (route: string) => void;
  onClick?: (e: React.MouseEvent) => void;
}

interface TagRowProps {
  t: Tag;
  data: TagData;
  onOpen: (tag: Tag) => void;
}

interface TagDetailPanelProps {
  open: boolean;
  tag: Tag | null;
  data: TagData;
  onClose: () => void;
  navigate: (route: string) => void;
}

interface SectionProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}

// ── Utility Functions ────────────────────────────────────────────────────────

export function tagDef(tagId: string, data: TagData): Tag | null {
  return data.tags?.tags.find((t) => t.id === tagId) || null;
}

export function kindDef(kindId: string, data: TagData): TagKind | null {
  return data.tags?.kinds.find((k) => k.id === kindId) || null;
}

// Compute how many entities a tag is attached to.
export function tagUsage(tagId: string, data: TagData): TagUsage {
  const repos = (data.repos?.repos || []).filter((r) => r.tags?.includes(tagId));
  const controls = (data.controls?.controls || []).filter((c) => c.tags?.includes(tagId));
  const projects = (data.projects?.projects || []).filter((p) => p.tags?.includes(tagId));
  const requirements: Requirement[] = [];
  
  // Behavior tags annotate requirements via the framework mapping; in this mock
  // we infer "this requirement has tag X" from a parallel mapping table below.
  const REQ_TAG_MAP: Record<string, Requirement[]> = {
    encryption: [
      { framework: 'soc2-ii', id: 'CC6.6' },
      { framework: 'iso-27001', id: 'A.8.24' },
      { framework: 'gdpr', id: 'Art. 32(1)(a)' },
      { framework: 'pci-dss', id: 'Req. 3' },
    ],
    'access-control': [
      { framework: 'soc2-ii', id: 'CC6.1' },
      { framework: 'iso-27001', id: 'A.5.15' },
      { framework: 'pci-dss', id: 'Req. 7' },
    ],
    'audit-logging': [
      { framework: 'soc2-ii', id: 'CC7.1' },
      { framework: 'soc2-ii', id: 'CC7.2' },
      { framework: 'iso-27001', id: 'A.8.15' },
      { framework: 'pci-dss', id: 'Req. 10' },
    ],
    'vuln-management': [
      { framework: 'soc2-ii', id: 'CC8.1' },
      { framework: 'iso-27001', id: 'A.8.8' },
      { framework: 'pci-dss', id: 'Req. 6' },
    ],
    'data-retention': [
      { framework: 'gdpr', id: 'Art. 5(1)(e)' },
      { framework: 'gdpr', id: 'Art. 17' },
      { framework: 'iso-27001', id: 'A.8.10' },
    ],
    backup: [
      { framework: 'soc2-ii', id: 'A1.2' },
      { framework: 'iso-27001', id: 'A.8.13' },
    ],
  };
  
  (REQ_TAG_MAP[tagId] || []).forEach((r) => requirements.push(r));
  
  const rules = (data.conformance?.rules || []).filter((rule) => {
    // Tag-based rules reference behavior tags through their operands
    if (rule.kind === 'tag-based') {
      const ops = rule.condition.operands || [];
      return ops.some((o) => o.tag === tagId);
    }
    return false;
  });
  
  return { repos, controls, projects, requirements, rules };
}

// ── Components ───────────────────────────────────────────────────────────────

// Reusable inline pill that resolves a tag's kind/color.
export function TagPill({ tagId, data, navigate, onClick }: TagPillProps) {
  const t = tagDef(tagId, data);
  const k = t ? kindDef(t.kind, data) : null;
  const color = k?.color || '#6B7280';
  
  const handle = (e: React.MouseEvent) => {
    if (onClick) {
      onClick(e);
      return;
    }
    if (navigate) {
      e.stopPropagation?.();
      navigate(`tags/${tagId}`);
    }
  };
  
  return (
    <button
      type="button"
      onClick={handle}
      title={t ? `${t.id} · ${k?.label || 'tag'}` : tagId}
      className="inline-flex items-center gap-1.5 h-6 px-1.5 rounded-[5px] hover:opacity-90"
      style={{ background: `${color}1a`, color }}
    >
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
      <span className="font-data text-[11px] font-bold">{tagId}</span>
    </button>
  );
}

function TagRow({ t, data, onOpen }: TagRowProps) {
  const k = kindDef(t.kind, data);
  const usage = tagUsage(t.id, data);
  const total =
    usage.repos.length + usage.controls.length + usage.projects.length + usage.requirements.length;
  
  return (
    <button
      type="button"
      onClick={() => onOpen(t)}
      className="grid items-center px-5 py-4 border-b border-[#F7EDDB] last:border-0 hover:bg-[#FCF8F1] text-left w-full"
      style={{ gridTemplateColumns: '1.4fr 0.9fr 1.6fr 1fr 60px' }}
    >
      <div className="flex items-center gap-2.5 min-w-0">
        <span
          className="h-2.5 w-2.5 rounded-full shrink-0"
          style={{ background: k?.color || '#6B7280' }}
        />
        <div className="min-w-0">
          <div className="font-display font-bold text-ink text-[14px] truncate">{t.id}</div>
          <div className="font-data text-[11px] text-ink/55 mt-0.5 truncate">{t.description}</div>
        </div>
      </div>
      <span className="inline-flex items-center gap-1.5">
        <span
          className="font-data text-[11px] font-bold px-1.5 py-[2px] rounded-[3px]"
          style={{
            background: `${k?.color || '#6B7280'}1a`,
            color: k?.color || '#6B7280',
          }}
        >
          {k?.label || t.kind}
        </span>
      </span>
      <div className="flex gap-1 flex-wrap">
        {t.appliesTo.map((a) => (
          <span
            key={a}
            className="inline-flex items-center gap-1 font-data text-[11px] px-1.5 py-[2px] rounded-[3px] bg-[#F7EDDB] text-ink/65"
          >
            <Icon
              name={
                a === 'repo'
                  ? 'book'
                  : a === 'control'
                    ? 'shield'
                    : a === 'project'
                      ? 'book'
                      : 'shield'
              }
              size={10}
              stroke="rgba(18,17,17,0.55)"
            />
            {a}
          </span>
        ))}
      </div>
      <span className="font-display font-bold text-ink text-[14px]">
        {total}
        <span className="font-data text-[11px] font-normal text-ink/55 ml-1">uses</span>
      </span>
      <Icon name="chevron-right" size={14} stroke="rgba(18,17,17,0.4)" />
    </button>
  );
}

function TagDetailPanel({ open, tag, data, onClose, navigate }: TagDetailPanelProps) {
  if (!tag) return null;
  const k = kindDef(tag.kind, data);
  const usage = tagUsage(tag.id, data);
  
  return (
    <ModalShell open={open} onClose={onClose} width={760}>
      <ModalHeader
        title={tag.id}
        subtitle={`${k?.label || tag.kind} tag · ${tag.appliesTo.join(', ')}`}
        onClose={onClose}
        icon="tag"
      />
      <div className="px-6 py-5 flex flex-col gap-5 overflow-y-auto">
        <p className="font-display text-[14px] text-ink/75">{tag.description}</p>

        <div className="grid grid-cols-4 gap-3">
          <Stat
            label="Repos"
            value={usage.repos.length}
            tone={usage.repos.length ? 'info' : 'muted'}
          />
          <Stat
            label="Controls"
            value={usage.controls.length}
            tone={usage.controls.length ? 'info' : 'muted'}
          />
          <Stat
            label="Projects"
            value={usage.projects.length}
            tone={usage.projects.length ? 'info' : 'muted'}
          />
          <Stat
            label={tag.kind === 'behavior' ? 'Requirements' : 'Rules'}
            value={tag.kind === 'behavior' ? usage.requirements.length : usage.rules.length}
            tone={
              (tag.kind === 'behavior' ? usage.requirements.length : usage.rules.length)
                ? 'info'
                : 'muted'
            }
          />
        </div>

        {usage.repos.length > 0 && (
          <Section title="On repositories">
            <ul className="flex flex-col">
              {usage.repos.map((r) => (
                <li key={r.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      navigate(`repositories/${r.id}`);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-[6px] hover:bg-[#FCF8F1] text-left"
                  >
                    <Icon name="book" size={14} stroke="#121111" />
                    <span className="font-display font-bold text-ink text-[13px] flex-1">
                      {r.name}
                    </span>
                    <LangDot lang={r.lang} />
                    <Icon name="chevron-right" size={12} stroke="rgba(18,17,17,0.4)" />
                  </button>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {usage.projects.length > 0 && (
          <Section title="On projects">
            <ul className="flex flex-col">
              {usage.projects.map((p) => (
                <li key={p.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      navigate(`projects/${p.id}`);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-[6px] hover:bg-[#FCF8F1] text-left"
                  >
                    <span
                      className="h-6 w-6 rounded-[6px] grid place-items-center"
                      style={{ background: `${p.color}1a` }}
                    >
                      <Icon name="book" size={12} stroke={p.color} />
                    </span>
                    <span className="font-display font-bold text-ink text-[13px] flex-1">
                      {p.name}
                    </span>
                    <Icon name="chevron-right" size={12} stroke="rgba(18,17,17,0.4)" />
                  </button>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {usage.controls.length > 0 && (
          <Section title="On controls">
            <ul className="flex flex-col">
              {usage.controls.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      navigate(`controls/${c.id}`);
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-[6px] hover:bg-[#FCF8F1] text-left"
                  >
                    <span className="font-data text-[11px] px-1.5 py-[2px] rounded-[3px] bg-[#EDE5FC] text-[#7F56D9]">
                      #{c.id}
                    </span>
                    <span className="font-display font-bold text-ink text-[13px] flex-1">
                      {c.name}
                    </span>
                    <ControlStatusBadge status={c.status} />
                  </button>
                </li>
              ))}
            </ul>
          </Section>
        )}

        {usage.requirements.length > 0 && (
          <Section
            title="On framework requirements"
            subtitle="Behavior tags annotate requirements via the framework mapping."
          >
            <ul className="flex flex-col">
              {usage.requirements.map((r) => {
                const f = data.frameworks.frameworks.find((x) => x.id === r.framework);
                return (
                  <li key={`${r.framework}-${r.id}`}>
                    <button
                      type="button"
                      onClick={() => {
                        onClose();
                        navigate(`frameworks/${r.framework}`);
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2 rounded-[6px] hover:bg-[#FCF8F1] text-left"
                    >
                      <span
                        className="h-6 w-6 rounded-[6px] grid place-items-center"
                        style={{ background: `${f?.color || '#6B7280'}1a` }}
                      >
                        <Icon name="shield" size={12} stroke={f?.color || '#6B7280'} />
                      </span>
                      <span className="font-display font-bold text-ink text-[13px] w-[110px]">
                        {f?.shortName}
                      </span>
                      <span className="font-data text-[12px] text-ink/70 flex-1">{r.id}</span>
                      <Icon name="chevron-right" size={12} stroke="rgba(18,17,17,0.4)" />
                    </button>
                  </li>
                );
              })}
            </ul>
          </Section>
        )}

        {usage.rules.length > 0 && (
          <Section title="Powering conformance rules">
            <ul className="flex flex-col">
              {usage.rules.map((rule) => (
                <li key={rule.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      navigate('conformance');
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-[6px] hover:bg-[#FCF8F1] text-left"
                  >
                    <Icon name="git-branch" size={14} stroke="#7F56D9" />
                    <span className="font-display font-bold text-ink text-[13px] flex-1">
                      {rule.name}
                    </span>
                    <span className="font-data text-[11px] text-ink/55">{rule.kind}</span>
                  </button>
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>
      <div className="px-6 py-4 border-t border-[#F7EDDB] flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="h-10 px-4 rounded-[8px] border border-[#F3E4C8] font-display font-semibold text-[14px] hover:bg-[#FCF8F1]"
        >
          Close
        </button>
        <button
          type="button"
          onClick={() => (window as any).openCreate?.('tag', { editing: true })}
          className="h-10 px-4 rounded-[8px] bg-[#FA500E] text-white font-display font-bold text-[14px] hover:opacity-90 whitespace-nowrap"
        >
          Edit tag
        </button>
      </div>
    </ModalShell>
  );
}

function Section({ title, subtitle, children }: SectionProps) {
  return (
    <div>
      <div className="font-display font-bold text-ink text-[13px]">{title}</div>
      {subtitle && <div className="font-data text-[12px] text-ink/55 mt-0.5">{subtitle}</div>}
      <div className="mt-2 rounded-[8px] border border-[#F3E4C8] bg-white">{children}</div>
    </div>
  );
}

export default function Tags({ data, navigate, params }: TagsProps) {
  const [opened, setOpened] = React.useState<Tag | null>(null);
  const [q, setQ] = React.useState('');
  const [kindFilter, setKindFilter] = React.useState('all');

  // Deep-link: /tags/<id>
  React.useEffect(() => {
    if (params?.[0] && data.tags) {
      const t = tagDef(params[0], data);
      if (t) setOpened(t);
    }
  }, [params?.[0], data]);

  if (!data.tags) return <div className="font-display text-ink/55">No tag data loaded.</div>;
  
  const { kinds, tags } = data.tags;
  const list = tags.filter((t) => {
    if (kindFilter !== 'all' && t.kind !== kindFilter) return false;
    if (!q.trim()) return true;
    return `${t.id} ${t.description || ''}`.toLowerCase().includes(q.toLowerCase());
  });

  return (
    <div className="flex flex-col gap-8">
      <PageHeader
        title="Tags"
        subtitle="Cross-cutting taxonomy. Tags carry semantic meaning by kind — behavior tags power conformance rules; compliance tags map to frameworks; environment tags shape risk scoring."
        right={
          <button
            type="button"
            className="h-10 px-4 rounded-[10px] bg-[#FA500E] text-white font-display font-bold text-[15px] inline-flex items-center gap-2 hover:opacity-90 whitespace-nowrap shrink-0"
          >
            <Icon name="plus" size={16} stroke="#FFF" /> New tag
          </button>
        }
      />

      {/* Kind overview */}
      <div className="grid grid-cols-6 gap-3">
        {kinds.map((k) => {
          const n = tags.filter((t) => t.kind === k.id).length;
          return (
            <button
              type="button"
              key={k.id}
              onClick={() => setKindFilter(k.id === kindFilter ? 'all' : k.id)}
              className={cx(
                'rounded-[10px] border bg-white p-4 text-left transition flex flex-col gap-1.5',
                kindFilter === k.id ? 'border-ink' : 'border-[#F3E4C8] hover:border-ink/40'
              )}
            >
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: k.color }} />
                <span className="font-display font-bold text-ink text-[14px]">{k.label}</span>
              </div>
              <p className="font-data text-[11px] text-ink/55 line-clamp-2 leading-snug">
                {k.description}
              </p>
              <div className="font-display font-bold text-ink mt-1 text-[16px]">
                {n}
                <span className="font-data text-[11px] font-normal text-ink/55 ml-1">tags</span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-3 rounded-[4px] border border-[rgba(18,17,17,0.2)] px-5 h-11">
        <div className="flex items-center gap-3 flex-1">
          <Icon name="search" size={16} stroke="#121111" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Find a tag…"
            className="flex-1 bg-transparent outline-none font-display text-[14px] placeholder:text-ink/45"
          />
        </div>
        {q && (
          <button type="button" onClick={() => setQ('')}>
            <Icon name="x" size={16} stroke="#121111" />
          </button>
        )}
      </div>

      <div className="rounded-[6px] border border-[#F3E4C8] bg-white overflow-hidden">
        <div
          className="grid items-center px-5 h-[54px] border-b border-[#F7EDDB] bg-[#FCF8F1] font-display font-bold text-[13px] text-ink"
          style={{ gridTemplateColumns: '1.4fr 0.9fr 1.6fr 1fr 60px' }}
        >
          <span>Tag</span>
          <span>Kind</span>
          <span>Applies to</span>
          <span>Usage</span>
          <span></span>
        </div>
        <ul>
          {list.map((t) => (
            <TagRow key={t.id} t={t} data={data} onOpen={setOpened} />
          ))}
        </ul>
      </div>

      <TagDetailPanel
        open={!!opened}
        tag={opened}
        data={data}
        onClose={() => setOpened(null)}
        navigate={navigate}
      />
    </div>
  );
}